import React, { useState, useEffect } from 'react';
import useAutoSave, { autoSaveLabel } from '../hooks/useAutoSave';
import './ModulePage.css';
import api from '../services/api';
import HomeButton from './HomeButton';
import { getModuleIconSrc } from '../assets/moduleIcons';
import InvoiceLedgerEdit from './InvoiceLedgerEdit';
import NotesTabsField, { notesTabsToPlainText } from './NotesTabsField';
import PersistentTabNotes from './PersistentTabNotes';
import { TaskConversationBubble } from './TaskConversation';
import TaskSubtasks from './TaskSubtasks';
import { setBreadcrumb } from '../services/breadcrumb';
import calendarService from '../services/calendarService';

const CHAT_API_BASE = '/api/chat';

// Authenticated fetch (Bearer) — used for the Lead attachments endpoint.
async function authFetch(url, opts = {}) {
  const token = await api.ensureAuthenticated();
  return fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
}

// Emoji icon for an attachment based on its MIME type.
const attIcon = (mime) => {
  if (!mime) return '📎';
  if (mime.startsWith('image/')) return '🖼️';
  if (mime === 'application/pdf') return '📄';
  if (mime.includes('word')) return '📝';
  if (mime.includes('sheet') || mime.includes('excel')) return '📊';
  return '📎';
};

// Lead pipeline statuses (same order the Leads list groups by).
const LEAD_STATUSES = ['Form Submission', 'Discovery Conducted', 'Consultation Pending', 'LOA Sent', 'Converted', 'Declined'];

// --- Meeting tile timing rules -------------------------------------------
// A meeting is archived (and hidden) once it ended more than 3 days ago.
// Otherwise the tile border signals timing:
//   - ended within the last 3 days  -> light gray  (meeting-ended-recent)
//   - not yet ended, starts <=36h    -> red         (meeting-start-soon)
//   - not yet ended, starts >36h      -> yellow      (meeting-start-far)
const MEETING_ARCHIVE_MS = 3 * 24 * 60 * 60 * 1000; // 3 days
const MEETING_SOON_MS = 36 * 60 * 60 * 1000;        // 36 hours
// Meeting times are stored and edited as naive wall-clock (the edit form uses a
// timezone-naive datetime-local input), so we compare them as LOCAL time. The
// V8 API tags them UTC ("…+00:00"); strip any timezone suffix and parse the
// wall-clock components in the viewer's local zone so the tiles agree with what
// the user sees and enters.
const parseMeetingDate = (v) => {
  if (!v) return null;
  const m = String(v).trim().match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (m) {
    const d = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};
// Start/end, with each falling back to the other when a meeting only has one
// (e.g. a meeting recorded with just an end time).
const meetingTimes = (record) => {
  let start = parseMeetingDate(record?.attributes?.date_start);
  let end = parseMeetingDate(record?.attributes?.date_end);
  if (!start) start = end;
  if (!end) end = start;
  return { start, end };
};
// True once the meeting ended more than 3 days ago -> archive + hide.
const isMeetingStale = (record) => {
  const { end } = meetingTimes(record);
  if (!end) return false;
  return (Date.now() - end.getTime()) > MEETING_ARCHIVE_MS;
};
// Border class for a (non-stale) meeting tile. Precedence:
//   1. past the end time (meeting is over)                       -> gray
//   2. within the window from 36h before start through end time  -> red
//   3. more than 36h before start (upcoming later)               -> yellow
// (Meetings ended more than 3 days ago are archived/hidden before this runs.)
const meetingTileClass = (record) => {
  const now = Date.now();
  const { start, end } = meetingTimes(record);
  if (end && now > end.getTime()) return 'meeting-ended-recent'; // gray — past the end time
  if (start && now >= start.getTime() - MEETING_SOON_MS) return 'meeting-start-soon'; // red — 36h before start .. end
  if (start) return 'meeting-start-far'; // yellow — more than 36h before start
  return '';
};

// Parse a form datetime ("YYYY-MM-DD HH:MM[:SS]" or with a 'T") as local time.
const parseFormDateTime = (v) => {
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) : null;
};
const fmtLocalDateTime = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:00`;
};
// SuiteCRM derives a Meeting's date_end from date_start + duration, so a form
// that edits Start Date and End Date as independent fields must translate them
// into a duration on save — otherwise the end change is discarded, and a
// meeting with no start (e.g. one recorded with only an end) keeps a null
// start. Normalize both datetimes, send date_start + duration, and drop
// date_end so SuiteCRM derives it from the pair we just made consistent.
// Mutates mainFormData in place.
const finalizeMeetingFields = (mainFormData, rawFormData) => {
  let start = parseFormDateTime(rawFormData.date_start);
  let end = parseFormDateTime(rawFormData.date_end);
  if (!start && !end) return;          // nothing to reconcile
  if (!start) start = end;             // only end given -> point-in-time meeting
  if (!end) end = start;               // only start given -> point-in-time meeting
  let mins = Math.round((end.getTime() - start.getTime()) / 60000);
  if (mins < 0) mins = 0;              // guard an end that precedes the start
  mainFormData.date_start = fmtLocalDateTime(start);
  mainFormData.duration_hours = String(Math.floor(mins / 60));
  mainFormData.duration_minutes = String(mins % 60);
  delete mainFormData.date_end;        // derived by SuiteCRM from start + duration
};

const ModulePage = ({ moduleName, onBack, currentUser, initialRecordId, onInitialConsumed }) => {
  const openedInitialRef = React.useRef(null);
  const [records, setRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [contactRoles, setContactRoles] = useState([]);
  const [selectedContactRole, setSelectedContactRole] = useState('');

  // New state for tabs and editing
  const [activeTab, setActiveTab] = useState('list');
  const [moduleFields, setModuleFields] = useState([]);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  // Breadcrumb: <Module> [→ <record>] when editing a record. getModuleDisplayName
  // is a hoisted function declaration, so it's available here.
  useEffect(() => {
    const label = getModuleDisplayName(moduleName);
    if (activeTab === 'edit' && editingRecord) {
      const a = editingRecord.attributes || {};
      const rec = (a.name && a.name.trim())
        || `${a.first_name || ''} ${a.last_name || ''}`.trim()
        || (editingRecord.id ? `#${String(editingRecord.id).slice(0, 8)}` : '');
      setBreadcrumb(rec ? [label, rec] : [label]);
    } else {
      setBreadcrumb([label]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleName, activeTab, editingRecord]);
  const [saveMessage, setSaveMessage] = useState(null);
  const [deleting, setDeleting] = useState(null); // Track which record is being deleted
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // Track delete confirmation modal

  // Leads: collapsed status groups (Converted/Declined hidden by default)
  const [hiddenStatusGroups, setHiddenStatusGroups] = useState({ 'Converted': true, 'Declined': true });

  // Leads: convert-to-project dialog
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [convertPending, setConvertPending] = useState(null); // stores the save data while dialog is open
  const [convertName, setConvertName] = useState(''); // editable name for the created Project

  // Lead file attachments (uploaded via ledger-attachments.php, parent_type='Leads').
  const [attachments, setAttachments] = useState([]);
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const attInputRef = React.useRef(null);

  // Schedule-a-meeting (Leads): date/time + duration; the rest is derived
  // from the lead's fields and its tabbed notes.
  const [mtgWhen, setMtgWhen] = useState('');
  const [mtgDurH, setMtgDurH] = useState('1');
  const [mtgDurM, setMtgDurM] = useState('0');
  const [mtgPrefix, setMtgPrefix] = useState(''); // title prefix -> "<prefix> - First Last"
  const [mtgSaving, setMtgSaving] = useState(false);
  const [mtgMsg, setMtgMsg] = useState(null);

  // Dynamic dropdown options (e.g., business types)
  const [dynamicOptions, setDynamicOptions] = useState({});
  // Task conversation replies, loaded for the standalone Tasks detail page.
  const [taskResponses, setTaskResponses] = useState([]);
  const [showAddOption, setShowAddOption] = useState({});
  const [newOptionValue, setNewOptionValue] = useState({});

  // Task project associations (record_id -> { project_id, project_name })
  const [taskProjects, setTaskProjects] = useState({});
  // Ids of tasks that are subtasks — hidden from the flat list (they show nested).
  const [subtaskIds, setSubtaskIds] = useState(() => new Set());
  // Ancestor tasks when drilling into subtasks (breadcrumb back to the parent).
  const [taskStack, setTaskStack] = useState([]);
  // Lead whose status is currently being auto-saved from its card dropdown.
  const [savingStatusId, setSavingStatusId] = useState(null);

  const tabs = [
    { id: 'list', label: getModuleDisplayName(moduleName), icon: getModuleIcon(moduleName) },
    ...(editingRecord ? [{ id: 'edit', label: 'Edit Record', icon: '✏️' }] : []),
  ];

  useEffect(() => {
    fetchRecords();
    fetchModuleFields();
  }, [moduleName, currentPage]);

  // Fetch dynamic options when module fields are loaded
  useEffect(() => {
    const fetchDynamicOptions = async () => {
      // Handle dynamic_enum fields (business types, etc.)
      const dynamicFields = moduleFields.filter(f => f.type === 'dynamic_enum');
      for (const field of dynamicFields) {
        if (field.options_source === 'business_types') {
          try {
            const data = await api.chatRequest('GET', `${CHAT_API_BASE}/businesses.php?action=types`);
            if (data.success && data.data) {
              setDynamicOptions(prev => ({
                ...prev,
                [field.name]: data.data.map(t => t.name)
              }));
            }
          } catch (err) {
            console.error('Failed to fetch business types:', err);
          }
        } else if (field.options_source === 'businesses') {
          try {
            const data = await api.chatRequest('GET', `${CHAT_API_BASE}/accounting.php?action=businesses`);
            if (data.success && data.data) {
              setDynamicOptions(prev => ({
                ...prev,
                [field.name]: data.data
              }));
            }
          } catch (err) {
            console.error('Failed to fetch businesses:', err);
          }
        } else if (field.options_source === 'contact_roles') {
          try {
            const data = await api.chatRequest('GET', `${CHAT_API_BASE}/contact-roles.php`);
            if (data.success && data.data) {
              setDynamicOptions(prev => ({
                ...prev,
                [field.name]: data.data.map(r => r.name)
              }));
            }
          } catch (err) {
            console.error('Failed to fetch contact roles:', err);
          }
        } else if (field.options_source === 'projects') {
          try {
            const response = await fetch(`${CHAT_API_BASE}/project-list.php`);
            const data = await response.json();
            if (data.success && data.data) {
              setDynamicOptions(prev => ({
                ...prev,
                [field.name]: data.data.map(p => p.name)
              }));
            }
          } catch (err) {
            console.error('Failed to fetch projects:', err);
          }
        } else if (field.options_source === 'users') {
          try {
            const data = await api.chatRequest('GET', `${CHAT_API_BASE}/users.php`);
            if (data.success && data.users) {
              setDynamicOptions(prev => ({
                ...prev,
                [field.name]: data.users.map(u => ({ id: u.id, name: u.name, username: u.username }))
              }));
            }
          } catch (err) {
            console.error('Failed to fetch users:', err);
          }
        }
      }

      // Handle project_relate fields - fetch projects list
      const projectFields = moduleFields.filter(f => f.type === 'project_relate');
      if (projectFields.length > 0) {
        try {
          const response = await fetch(`${CHAT_API_BASE}/project-list.php`);
          const data = await response.json();
          if (data.success && data.data) {
            // Store projects as array of {id, name} objects
            setDynamicOptions(prev => ({
              ...prev,
              projects: data.data
            }));
          }
        } catch (err) {
          console.error('Failed to fetch projects:', err);
        }
      }
    };

    if (moduleFields.length > 0) {
      fetchDynamicOptions();
    }
  }, [moduleFields]);

  const addNewDynamicOption = async (fieldName, optionsSource) => {
    const newValue = newOptionValue[fieldName]?.trim();
    if (!newValue) return;

    let endpoint = null;
    let errorLabel = optionsSource;
    if (optionsSource === 'business_types') {
      endpoint = `${CHAT_API_BASE}/businesses.php?action=types`;
      errorLabel = 'business type';
    } else if (optionsSource === 'contact_roles') {
      endpoint = `${CHAT_API_BASE}/contact-roles.php`;
      errorLabel = 'contact role';
    } else if (optionsSource === 'projects') {
      endpoint = `${CHAT_API_BASE}/project-list.php`;
      errorLabel = 'project';
    }

    if (endpoint) {
      try {
        const data = await api.chatRequest('POST', endpoint, { name: newValue });
        if (data.success) {
          setDynamicOptions(prev => ({
            ...prev,
            [fieldName]: [...(prev[fieldName] || []), newValue].sort()
          }));
          handleInputChange(fieldName, newValue);
          setNewOptionValue(prev => ({ ...prev, [fieldName]: '' }));
          setShowAddOption(prev => ({ ...prev, [fieldName]: false }));
        } else {
          alert(data.message || `Failed to add ${errorLabel}`);
        }
      } catch (err) {
        console.error(`Failed to add ${errorLabel}:`, err);
        alert(`Failed to add ${errorLabel}`);
      }
    }
  };

  // Filter records when category selection changes (for Products)
  useEffect(() => {
    if (moduleName === 'AOS_Products' && allRecords.length > 0) {
      if (selectedCategory) {
        const filtered = allRecords.filter(r => r.attributes?.category === selectedCategory);
        setRecords(filtered);
      } else {
        setRecords(allRecords);
      }
    }
  }, [selectedCategory, allRecords, moduleName]);

  // Filter records when contact role selection changes (for Contacts)
  useEffect(() => {
    if (moduleName === 'Contacts' && allRecords.length > 0) {
      if (selectedContactRole) {
        const filtered = allRecords.filter(r => r.attributes?.contact_role_c === selectedContactRole);
        setRecords(filtered);
      } else {
        setRecords(allRecords);
      }
    }
  }, [selectedContactRole, allRecords, moduleName]);

  // Fetch contact roles for filter dropdown (Contacts only)
  useEffect(() => {
    if (moduleName !== 'Contacts') return;
    (async () => {
      try {
        const data = await api.chatRequest('GET', `${CHAT_API_BASE}/contact-roles.php`);
        if (data.success && data.data) {
          setContactRoles(data.data.map(r => r.name));
        }
      } catch (e) { /* ignore */ }
    })();
  }, [moduleName]);

  // Filter records based on search query
  useEffect(() => {
    // Hide subtasks from the flat Tasks list — they appear nested under their parent.
    let base = (moduleName === 'Tasks' && subtaskIds.size)
      ? records.filter(r => !subtaskIds.has(r.id))
      : records;

    // Hide meetings archived by the 3-day rule (they ended more than 3 days ago).
    if (moduleName === 'Meetings') {
      base = base.filter(r => !isMeetingStale(r));
    }

    if (!searchQuery.trim()) {
      setFilteredRecords(base);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = base.filter(record => {
      // Search through all attributes
      if (record.attributes) {
        for (const [key, value] of Object.entries(record.attributes)) {
          if (value !== null && value !== undefined) {
            const stringValue = String(value).toLowerCase();
            if (stringValue.includes(query)) {
              return true;
            }
          }
        }
      }
      // Also search the record ID
      if (record.id && record.id.toLowerCase().includes(query)) {
        return true;
      }
      return false;
    });

    setFilteredRecords(filtered);
  }, [searchQuery, records, subtaskIds, moduleName]);

  // Persist the archive flag for meetings that ended more than 3 days ago, so
  // the record itself is marked archived (not just hidden client-side). The
  // endpoint is idempotent, so re-posting an already-archived id is a no-op.
  useEffect(() => {
    if (moduleName !== 'Meetings' || records.length === 0) return;
    const staleIds = records.filter(isMeetingStale).map(r => r.id);
    if (staleIds.length === 0) return;
    (async () => {
      try {
        const token = await api.ensureAuthenticated();
        await fetch(`${CHAT_API_BASE}/meetings-archive.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ meeting_ids: staleIds, user_id: currentUser?.id }),
        });
      } catch (e) {
        console.error('Failed to archive stale meetings:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records, moduleName]);

  const fetchModuleFields = async () => {
    try {
      const response = await fetch(`${CHAT_API_BASE}/module-fields.php?module=${moduleName}`);
      const data = await response.json();
      if (data.success && data.fields) {
        setModuleFields(data.fields);
        // Initialize empty form data based on fields
        const initialFormData = {};
        data.fields.forEach(field => {
          initialFormData[field.name] = '';
        });
        setFormData(initialFormData);
      }
    } catch (err) {
      console.error('Failed to fetch module fields:', err);
    }
  };

  const fetchRecords = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.getRecords(moduleName, {
        page: currentPage,
        pageSize: 20,
        sort: '-date_entered',
        userId: currentUser?.id,
      });

      // Extract records from JSON API format
      const recordList = response.data || [];

      // For Products, store all records and extract categories
      if (moduleName === 'AOS_Products') {
        setAllRecords(recordList);

        // Extract unique categories
        const uniqueCategories = [...new Set(
          recordList
            .map(r => r.attributes?.category)
            .filter(c => c && c.trim())
        )].sort();
        setCategories(uniqueCategories);

        // Apply filter if one is selected
        if (selectedCategory) {
          const filtered = recordList.filter(r => r.attributes?.category === selectedCategory);
          setRecords(filtered);
        } else {
          setRecords(recordList);
        }
      } else if (moduleName === 'Contacts') {
        setAllRecords(recordList);
        if (selectedContactRole) {
          const filtered = recordList.filter(r => r.attributes?.contact_role_c === selectedContactRole);
          setRecords(filtered);
        } else {
          setRecords(recordList);
        }
      } else {
        setRecords(recordList);
      }

      // Calculate total pages if meta is available
      if (response.meta && response.meta.total) {
        const total = Math.ceil(response.meta.total / 20);
        setTotalPages(total);
      }

      // Fetch project associations for Tasks
      if (moduleName === 'Tasks' && recordList.length > 0) {
        try {
          const projResponse = await fetch(`${CHAT_API_BASE}/record-project.php?module=Tasks`);
          const projData = await projResponse.json();
          if (projData.success && projData.data) {
            setTaskProjects(projData.data);
          }
        } catch (err) {
          console.error('Failed to fetch task project associations:', err);
        }
        // Which of these tasks are subtasks? Hide them from the flat list.
        try {
          const token = await api.ensureAuthenticated();
          const subResp = await fetch(`${CHAT_API_BASE}/project-tasks.php?all_subtask_ids=1`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const subData = await subResp.json();
          if (subData.success && Array.isArray(subData.ids)) setSubtaskIds(new Set(subData.ids));
        } catch (err) {
          console.error('Failed to fetch subtask ids:', err);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error(`Failed to fetch ${moduleName} records:`, err);
      setError(`Failed to load ${moduleName} records. Please try again.`);
      setLoading(false);
    }
  };

  function getModuleIcon(moduleName) {
    const pngSrc = getModuleIconSrc(moduleName);
    if (pngSrc) {
      return <img src={pngSrc} alt={moduleName} className="module-png-icon" />;
    }
    const icons = {
      'Accounts': '🏢',
      'Contacts': '👤',
      'Leads': '🎯',
      'Opportunities': '💰',
      'Cases': '📋',
      'Tasks': '✓',
      'Calls': '📞',
      'Meetings': '📅',
      'Notes': '📝',
      'Documents': '📄',
      'Campaigns': '📢',
      'Project': '🏗️',
      'ProjectTask': '📋',
      'AOS_Products': '📦',
      'AOS_Quotes': '💵',
      'AOS_Invoices': '🧾',
      'AOS_Contracts': '📜',
      'Prospects': '🎯',
      'Bugs': '🐞',
      'jcrm_Businesses': '🏪',
      'jcrm_Accounting': '📒',
      'FP_events': '🎪',
      'Emails': '✉️'
    };
    return icons[moduleName] || '📦';
  }

  function getModuleDisplayName(moduleName) {
    const displayNames = {
      'AOS_Products': 'Products',
      'AOS_Product_Categories': 'Categories',
      'AOS_Quotes': 'Proposals',
      'AOS_Invoices': 'Invoices',
      'AOS_Contracts': 'Contracts',
      'AOR_Reports': 'Reports',
      'jcrm_Businesses': 'Businesses',
      'jcrm_Accounting': 'Accounting',
      'jcrm_PurchaseOrders': 'Purchase Orders',
      'FP_events': 'Events',
      'AOS_PDF_Templates': 'PDF Templates',
      'ProspectLists': 'Target Lists',
      'Prospects': 'Targets',
      'ProjectTask': 'Project Tasks'
    };
    return displayNames[moduleName] || moduleName;
  }

  // Singular form of the module name, e.g. "Tasks" -> "Task", "AOS_Invoices" -> "Invoice".
  function getModuleSingularName(moduleName) {
    const overrides = {
      'AOS_Products': 'Product',
      'AOS_Product_Categories': 'Category',
      'AOS_Quotes': 'Proposal',
      'AOS_Invoices': 'Invoice',
      'AOS_Contracts': 'Contract',
      'AOR_Reports': 'Report',
      'jcrm_Businesses': 'Business',
      'jcrm_BusinessTypes': 'Business Type',
      'jcrm_Accounting': 'Accounting Entry',
      'jcrm_PurchaseOrders': 'Purchase Order',
      'FP_events': 'Event',
      'AOS_PDF_Templates': 'PDF Template',
      'ProspectLists': 'Target List',
      'Prospects': 'Target',
      'ProjectTask': 'Project Task',
      'Opportunities': 'Opportunity',
      'Cases': 'Case',
      'Meetings': 'Meeting',
      'Calls': 'Call',
      'Tasks': 'Task',
      'Notes': 'Note',
      'Contacts': 'Contact',
      'Accounts': 'Account',
      'Leads': 'Lead',
      'Documents': 'Document',
      'Campaigns': 'Campaign',
      'Project': 'Project',
    };
    if (overrides[moduleName]) return overrides[moduleName];
    // Fallback: strip trailing "ies" -> "y", "es" -> "", "s" -> ""
    const display = getModuleDisplayName(moduleName);
    if (display.endsWith('ies')) return display.slice(0, -3) + 'y';
    if (display.endsWith('ses') || display.endsWith('xes') || display.endsWith('zes') || display.endsWith('shes') || display.endsWith('ches')) {
      return display.slice(0, -2);
    }
    if (display.endsWith('s')) return display.slice(0, -1);
    return display;
  }

  const formatFieldValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return value.toString();
  };

  const getDisplayFields = (record) => {
    if (!record.attributes) return {};

    // Common fields to display for all modules
    const { name, first_name, last_name, email, phone_office, assigned_user_name, date_entered } = record.attributes;

    const fields = {};

    // Build name field
    if (name) {
      fields.Name = name;
    } else if (first_name || last_name) {
      fields.Name = `${first_name || ''} ${last_name || ''}`.trim();
    }

    if (email) fields.Email = email;
    if (phone_office) fields.Phone = phone_office;
    if (assigned_user_name) fields['Assigned To'] = assigned_user_name;
    if (date_entered) {
      const date = new Date(date_entered);
      fields.Created = date.toLocaleDateString();
    }

    return fields;
  };

  const handleEditRecord = async (record) => {
    setEditingRecord(record);
    // Populate form data from record attributes
    const recordData = {};
    moduleFields.forEach(field => {
      recordData[field.name] = record.attributes?.[field.name] || '';
    });

    // Fetch project_id from custom table for invoices/quotes/products
    if ((moduleName === 'AOS_Invoices' || moduleName === 'AOS_Quotes' || moduleName === 'AOS_Products' || moduleName === 'Tasks') && record.id) {
      try {
        const response = await fetch(`${CHAT_API_BASE}/record-project.php?module=${moduleName}&record_id=${record.id}`);
        const data = await response.json();
        if (data.success && data.data?.project_id) {
          recordData.project_id = data.data.project_id;
        }
      } catch (err) {
        console.error('Failed to fetch project_id:', err);
      }
    }

    setFormData(recordData);
    setActiveTab('edit');
    setSaveMessage(null);
  };

  // Deep link from the dashboard: open a specific record once module fields are
  // loaded (handleEditRecord needs them to map attributes). Runs once per id.
  useEffect(() => {
    if (!initialRecordId || moduleFields.length === 0) return;
    if (openedInitialRef.current === initialRecordId) return;
    openedInitialRef.current = initialRecordId;
    (async () => {
      try {
        let rec = records.find(r => r.id === initialRecordId);
        if (!rec) {
          const res = await api.getRecord(moduleName, initialRecordId);
          rec = res?.data || res;
        }
        if (rec && rec.id) await handleEditRecord(rec);
      } catch (e) {
        console.error('Failed to open record from dashboard:', e);
      }
      if (onInitialConsumed) onInitialConsumed();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRecordId, moduleFields]);

  // Inline status change from a Lead card — auto-saves immediately (optimistic,
  // reverts on failure).
  const updateLeadStatus = async (record, newStatus) => {
    const prevStatus = record.attributes?.status || '';
    if (!newStatus || newStatus === prevStatus) return;
    // Choosing "Converted" runs the full conversion flow — the same dialog the
    // edit form shows, offering to also create Account/Contact/Opportunity/Project.
    if (newStatus === 'Converted' && prevStatus !== 'Converted') {
      const a = record.attributes || {};
      setConvertPending({ source: 'card', record, formData: a });
      setConvertName(`${a.first_name || ''} ${a.last_name || ''}`.trim());
      setShowConvertDialog(true);
      return;
    }
    setSavingStatusId(record.id);
    const apply = (status) => setRecords(prev => prev.map(r =>
      r.id === record.id ? { ...r, attributes: { ...r.attributes, status } } : r));
    apply(newStatus); // optimistic
    try {
      await api.updateRecord('Leads', record.id, { status: newStatus });
    } catch (e) {
      apply(prevStatus); // revert
      alert('Failed to update status: ' + (e.message || 'error'));
    }
    setSavingStatusId(null);
  };

  // Convert a Lead straight from its card: set status to Converted and, when
  // confirmed, create the Account/Contact/Opportunity/Project via convert-lead.php
  // (same endpoint the edit form uses).
  const convertLeadFromCard = async (record, createProject) => {
    const prevStatus = record.attributes?.status || '';
    setSavingStatusId(record.id);
    const apply = (status) => setRecords(prev => prev.map(r =>
      r.id === record.id ? { ...r, attributes: { ...r.attributes, status } } : r));
    apply('Converted'); // optimistic
    try {
      await api.updateRecord('Leads', record.id, { status: 'Converted' });
      if (createProject) {
        const token = await api.ensureAuthenticated();
        const res = await fetch(`${CHAT_API_BASE}/convert-lead.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ lead_id: record.id, project_name: convertName.trim() || undefined, user_id: currentUser?.id }),
        });
        const data = await res.json().catch(() => ({}));
        if (!data.success) {
          alert('Status set to Converted, but creating the conversion records failed: ' + (data.message || 'unknown error'));
        }
        fetchRecords(); // reflect the newly linked records / back-filled fields
      }
    } catch (e) {
      apply(prevStatus); // revert
      alert('Failed to convert lead: ' + (e.message || 'error'));
    }
    setSavingStatusId(null);
  };

  // ─── Lead file attachments ──────────────────────────────────────────────
  // Load attachments whenever a Lead is opened for editing.
  useEffect(() => {
    if (moduleName !== 'Leads' || !editingRecord?.id) { setAttachments([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${CHAT_API_BASE}/ledger-attachments.php?parent_type=Leads&parent_id=${encodeURIComponent(editingRecord.id)}`);
        const data = await res.json();
        if (!cancelled && data.success) setAttachments(data.data || []);
      } catch (e) { if (!cancelled) setAttachments([]); }
    })();
    return () => { cancelled = true; };
  }, [moduleName, editingRecord]);

  const handleAttachChosen = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = ''; // allow re-selecting the same file
    if (!file || !editingRecord?.id) return;
    setUploadingAtt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name);
      fd.append('parent_type', 'Leads');
      fd.append('parent_id', editingRecord.id);
      const res = await authFetch(`${CHAT_API_BASE}/ledger-attachments.php`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success && data.data) {
        setAttachments(prev => [...prev, data.data]);
      } else {
        alert('Failed to attach file: ' + (data.message || 'unknown error'));
      }
    } catch (err) {
      console.error('Attachment upload failed:', err);
      alert('Failed to attach file.');
    }
    setUploadingAtt(false);
  };

  const deleteAttachment = async (attId) => {
    setAttachments(prev => prev.filter(a => a.id !== attId));
    try {
      await authFetch(`${CHAT_API_BASE}/ledger-attachments.php?id=${encodeURIComponent(attId)}`, { method: 'DELETE' });
    } catch (e) { console.error('Failed to delete attachment:', e); }
  };

  // Create a Meeting from the open Lead: date/time + duration are chosen here;
  // name/phone/address/notes are derived from the lead's fields.
  const scheduleLeadMeeting = async () => {
    setMtgMsg(null);
    if (!mtgWhen) { setMtgMsg({ type: 'error', text: 'Pick a date and time for the meeting.' }); return; }
    const fd = formData || {};
    const fullName = `${fd.first_name || ''} ${fd.last_name || ''}`.trim() || 'Lead';
    const phone = (fd.phone_mobile || '').trim() || (fd.phone_work || '').trim();
    const addrParts = [fd.primary_address_street, fd.primary_address_city, fd.primary_address_state,
      fd.primary_address_postalcode, fd.primary_address_country].map(v => (v || '').trim()).filter(Boolean);
    const location = addrParts.join(', ');
    const notes = notesTabsToPlainText(fd.description || '', 'Notes').trim();

    const descLines = [`Lead: ${fullName}`];
    if (phone) descLines.push(`Phone: ${phone}`);
    if ((fd.email1 || '').trim()) descLines.push(`Email: ${fd.email1.trim()}`);
    if (location) descLines.push(`Address: ${location}`);
    if ((fd.lead_source || '').trim()) descLines.push(`Lead Source: ${fd.lead_source.trim()}`);
    if (notes) descLines.push('', 'Notes:', notes);

    // datetime-local -> "YYYY-MM-DD HH:MM:SS"; end = start + duration.
    const start = mtgWhen.replace('T', ' ') + ':00';
    const startMs = new Date(mtgWhen).getTime();
    const durMs = ((parseInt(mtgDurH, 10) || 0) * 60 + (parseInt(mtgDurM, 10) || 0)) * 60000;
    const endD = new Date(startMs + durMs);
    const p = (n) => String(n).padStart(2, '0');
    const end = `${endD.getFullYear()}-${p(endD.getMonth() + 1)}-${p(endD.getDate())} ${p(endD.getHours())}:${p(endD.getMinutes())}:00`;

    // Title: "<prefix> - First Last" when a prefix is given, else the old default.
    const mtgTitlePrefix = (mtgPrefix || '').trim();
    const meetingTitle = mtgTitlePrefix ? `${mtgTitlePrefix} - ${fullName}` : `Meeting with ${fullName}`;

    setMtgSaving(true);
    try {
      const res = await calendarService.createEvent({
        type: 'meeting',
        title: meetingTitle,
        description: descLines.join('\n'),
        start,
        end,
        location,
        status: 'Planned',
        user_id: currentUser?.id,
        parent_type: 'Leads',
        parent_id: editingRecord?.id,
      });
      if (res && res.success) {
        setMtgMsg({ type: 'success', text: 'Meeting scheduled.' });
        setMtgWhen('');
      } else {
        setMtgMsg({ type: 'error', text: (res && res.message) || 'Failed to schedule meeting.' });
      }
    } catch (e) {
      setMtgMsg({ type: 'error', text: 'Failed to schedule meeting: ' + e.message });
    }
    setMtgSaving(false);
  };

  // Load the open task's conversation replies (task-responses.php requires auth).
  const loadTaskResponses = async () => {
    if (moduleName !== 'Tasks' || !editingRecord?.id) { setTaskResponses([]); return; }
    try {
      const token = await api.ensureAuthenticated();
      const res = await fetch(`${CHAT_API_BASE}/task-responses.php?task_id=${encodeURIComponent(editingRecord.id)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setTaskResponses(data.success ? (data.data || []) : []);
    } catch (e) { setTaskResponses([]); }
  };
  useEffect(() => { loadTaskResponses(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [moduleName, editingRecord]);

  // Delete the open task from its conversation bubble, then close the detail.
  const deleteThisTask = async () => {
    if (!editingRecord?.id) return;
    if (!window.confirm('Delete this task? Responses on it will remain in the record.')) return;
    try {
      const token = await api.ensureAuthenticated();
      const res = await fetch(`${CHAT_API_BASE}/project-tasks.php?id=${encodeURIComponent(editingRecord.id)}&user_id=${encodeURIComponent(currentUser?.id || '')}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) { alert(data.message || 'Failed to delete task'); return; }
      handleCancelEdit();
      fetchRecords();
    } catch (e) { alert('Failed to delete task: ' + e.message); }
  };

  // Drill into a subtask (a full task): remember the current task so the
  // breadcrumb can return to it, then open the subtask in the same detail.
  const openStandaloneSubtask = (subtask) => {
    setTaskStack(s => [...s, editingRecord]);
    handleEditRecord(subtask);
  };
  // Breadcrumb: pop one level back to the parent task (or exit to the list).
  const backToParentTask = () => {
    if (taskStack.length) {
      const parent = taskStack[taskStack.length - 1];
      setTaskStack(taskStack.slice(0, -1));
      handleEditRecord(parent);
    } else {
      handleCancelEdit();
    }
  };

  const handleNewRecord = () => {
    setEditingRecord(null);
    // Clear form data
    const emptyData = {};
    moduleFields.forEach(field => {
      emptyData[field.name] = '';
    });
    setFormData(emptyData);
    setActiveTab('edit');
    setSaveMessage(null);
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => {
      const next = { ...prev, [fieldName]: value };

      // Products: recalculate price from cost + markup
      if (moduleName === 'AOS_Products') {
        const cost = parseFloat(fieldName === 'cost' ? value : next.cost) || 0;

        if (fieldName === 'markup_percent_c') {
          const pct = parseFloat(value) || 0;
          next.markup_amount_c = '';
          next.price = (cost + (cost * pct / 100)).toFixed(2);
        } else if (fieldName === 'markup_amount_c') {
          const amt = parseFloat(value) || 0;
          next.markup_percent_c = '';
          next.price = (cost + amt).toFixed(2);
        } else if (fieldName === 'cost') {
          const pct = parseFloat(next.markup_percent_c) || 0;
          const amt = parseFloat(next.markup_amount_c) || 0;
          if (next.markup_percent_c !== '' && next.markup_percent_c != null) {
            next.price = (cost + (cost * pct / 100)).toFixed(2);
          } else if (next.markup_amount_c !== '' && next.markup_amount_c != null) {
            next.price = (cost + amt).toFixed(2);
          }
        }
      }

      return next;
    });
    // Meetings save explicitly (via "Done"): SuiteCRM derives date_end from
    // date_start + duration, so per-keystroke autosaves of one field at a time
    // would repeatedly overwrite the other. One consistent save avoids that.
    if (editingRecord?.id && moduleName !== 'Meetings') scheduleAutoSave();
  };

  const handleSaveRecord = async () => {
    // Check if this is a Lead being changed to "Converted" — prompt for project creation
    if (moduleName === 'Leads' && editingRecord && formData.status === 'Converted' && editingRecord.attributes?.status !== 'Converted') {
      setConvertPending({ formData: { ...formData }, editingRecord });
      setConvertName(`${formData.first_name || ''} ${formData.last_name || ''}`.trim());
      setShowConvertDialog(true);
      return;
    }

    await executeSaveRecord();
  };

  const executeSaveRecord = async (createProject = false) => {
    setSaving(true);
    setSaveMessage(null);

    try {
      // Extract project_id from formData for separate handling
      const { project_id, ...rawFormData } = formData;
      let recordId = editingRecord?.id;

      // Clean up form data - convert empty strings to null for date/currency fields
      // and remove fields that shouldn't be sent to the API
      const mainFormData = {};
      moduleFields.forEach(field => {
        if (field.name === 'project_id') return; // Skip project_id, handled separately
        const value = rawFormData[field.name];
        if (value === '' || value === undefined) {
          // Only include empty values for text fields, skip for dates/currency
          if (field.type === 'text' || field.type === 'textarea' || field.type === 'notes_tabs') {
            mainFormData[field.name] = '';
          }
          // Don't send empty date/currency fields
        } else {
          // Append seconds to datetime values if missing (V8 API requires HH:MM:SS)
          if (field.type === 'datetime' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(value)) {
            mainFormData[field.name] = value + ':00';
          } else {
            mainFormData[field.name] = value;
          }
        }
      });

      // Meetings: translate Start/End into date_start + duration (SuiteCRM
      // derives date_end from those), defaulting a missing start to the end.
      if (moduleName === 'Meetings') finalizeMeetingFields(mainFormData, rawFormData);

      if (editingRecord) {
        // Update existing record first so the conversion endpoint reads the
        // latest field values (the user may have edited email/notes/etc. before
        // flipping status to Converted).
        await api.updateRecord(moduleName, editingRecord.id, mainFormData);
        setSaveMessage({ type: 'success', text: 'Record updated successfully!' });
      } else if (moduleName === 'Tasks') {
        // Create Tasks through project-tasks.php so created_by is the real user
        // (the shared-apiuser V8 API would stamp "API User"). It also links the
        // project (tasks_cstm.project_c), so the record-project.php call below
        // is skipped for Task creates.
        const token = await api.ensureAuthenticated();
        const res = await fetch(`${CHAT_API_BASE}/project-tasks.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ ...mainFormData, project_id: project_id || null, created_by: currentUser?.id }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) throw new Error(data.message || 'Failed to create task');
        recordId = data?.data?.id;
        setSaveMessage({ type: 'success', text: 'Record created successfully!' });
      } else {
        // Create new record
        const result = await api.createRecord(moduleName, mainFormData);
        recordId = result?.data?.id;
        setSaveMessage({ type: 'success', text: 'Record created successfully!' });
      }

      // Stock-style Lead conversion: ask the backend to create Account +
      // Contact + Opportunity + Project (linked together) and back-fill the
      // Lead's converted/contact_id/account_id/opportunity_id columns.
      if (createProject && moduleName === 'Leads' && editingRecord?.id) {
        try {
          const token = await api.ensureAuthenticated();
          const convertRes = await fetch(`${CHAT_API_BASE}/convert-lead.php`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ lead_id: editingRecord.id, project_name: convertName.trim() || undefined, user_id: currentUser?.id }),
          });
          const convertData = await convertRes.json();
          if (convertData.success) {
            setSaveMessage({
              type: 'success',
              text: 'Lead converted — Account, Contact, Opportunity and Project created.',
            });
          } else {
            setSaveMessage({
              type: 'success',
              text: `Lead saved as Converted, but the conversion records failed: ${convertData.message || 'unknown error'}`,
            });
          }
        } catch (err) {
          console.error('Lead conversion failed:', err);
          setSaveMessage({
            type: 'success',
            text: 'Lead saved as Converted, but the conversion records failed to create.',
          });
        }
      }

      // Save project_id to custom table for invoices/quotes/products. Tasks only
      // need this on EDIT — a Task create already linked the project via
      // project-tasks.php above.
      if (((moduleName === 'AOS_Invoices' || moduleName === 'AOS_Quotes' || moduleName === 'AOS_Products')
           || (moduleName === 'Tasks' && editingRecord)) && recordId) {
        try {
          await fetch(`${CHAT_API_BASE}/record-project.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              module: moduleName,
              record_id: recordId,
              project_id: project_id || null
            })
          });
        } catch (err) {
          console.error('Failed to save project_id:', err);
        }
      }

      // Send @mention chat notification to assignee when Task is saved
      if (moduleName === 'Tasks' && mainFormData.assigned_user_id && currentUser?.id) {
        try {
          const assigneeId = mainFormData.assigned_user_id;
          // Only notify if assignee is different from current user
          if (assigneeId !== currentUser.id) {
            const usersOpts = dynamicOptions['assigned_user_id'] || [];
            const assignee = usersOpts.find(u => u.id === assigneeId);
            const taskName = mainFormData.name || 'Untitled Task';
            if (assignee?.username) {
              const message = `@${assignee.username} You have been assigned to task: ${taskName}`;
              await fetch(`${CHAT_API_BASE}/messages.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  sender_id: currentUser.id,
                  recipient_id: assigneeId,
                  message: message
                })
              });
            }
          }
        } catch (err) {
          console.error('Failed to send task assignment notification:', err);
        }
      }

      // Refresh records list
      await fetchRecords();

      // After short delay, switch to list tab
      setTimeout(() => {
        setActiveTab('list');
        setEditingRecord(null);
        const emptyData = {};
        moduleFields.forEach(field => {
          emptyData[field.name] = '';
        });
        setFormData(emptyData);
        setSaveMessage(null);
      }, 1500);

    } catch (err) {
      console.error('Failed to save record:', err);
      // Extract more detailed error message
      let errorMessage = 'Failed to save record. Please try again.';
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        if (Array.isArray(errors) && errors.length > 0) {
          errorMessage = errors.map(e => e.detail || e.title || e.message).join(', ');
        }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setSaveMessage({ type: 'error', text: errorMessage });
    }

    setSaving(false);
  };

  // Auto-save (edit mode only): persist the core field values via update, WITHOUT the
  // explicit-save side effects (navigation back to list, notifications, lead conversion,
  // project linkage) — those stay on the deliberate "Done" button.
  const autoSaveRecord = async () => {
    if (!editingRecord?.id) return;
    const { project_id, ...rawFormData } = formData; // eslint-disable-line no-unused-vars
    const mainFormData = {};
    moduleFields.forEach(field => {
      if (field.name === 'project_id') return;
      const value = rawFormData[field.name];
      if (value === '' || value === undefined) {
        if (field.type === 'text' || field.type === 'textarea' || field.type === 'notes_tabs') {
          mainFormData[field.name] = '';
        }
      } else if (field.type === 'datetime' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(value)) {
        mainFormData[field.name] = value + ':00';
      } else {
        mainFormData[field.name] = value;
      }
    });
    if (moduleName === 'Meetings') finalizeMeetingFields(mainFormData, rawFormData);
    await api.updateRecord(moduleName, editingRecord.id, mainFormData);
  };
  const { schedule: scheduleAutoSave, status: autoStatus } = useAutoSave(autoSaveRecord);

  const handleConvertYes = () => {
    const pending = convertPending;
    setShowConvertDialog(false);
    setConvertPending(null);
    if (pending?.source === 'card') { convertLeadFromCard(pending.record, true); return; }
    executeSaveRecord(true);
  };

  const handleConvertNo = () => {
    const pending = convertPending;
    setShowConvertDialog(false);
    setConvertPending(null);
    if (pending?.source === 'card') { convertLeadFromCard(pending.record, false); return; }
    executeSaveRecord(false);
  };

  const handleCancelEdit = () => {
    setEditingRecord(null);
    setTaskStack([]);
    const emptyData = {};
    moduleFields.forEach(field => {
      emptyData[field.name] = '';
    });
    setFormData(emptyData);
    setActiveTab('list');
    setSaveMessage(null);
  };

  const handleDeleteClick = (e, record) => {
    e.stopPropagation(); // Prevent triggering edit
    setShowDeleteConfirm(record);
  };

  const handleDeleteConfirm = async () => {
    if (!showDeleteConfirm) return;

    setDeleting(showDeleteConfirm.id);
    try {
      await api.deleteRecord(moduleName, showDeleteConfirm.id);
      setShowDeleteConfirm(null);
      // Refresh records list
      await fetchRecords();
    } catch (err) {
      console.error('Failed to delete record:', err);
      alert('Failed to delete record. Please try again.');
    }
    setDeleting(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(null);
  };

  const renderFormField = (field) => {
    const value = formData[field.name] || '';

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            className="form-textarea"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.label}
            rows={4}
          />
        );

      case 'notes_tabs':
        return (
          <NotesTabsField
            value={value}
            defaultTitle={field.default_tab_title || 'Description'}
            onChange={(json) => handleInputChange(field.name, json)}
          />
        );

      case 'enum':
        return (
          <select
            className="form-select"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
          >
            {field.options?.map(opt => (
              <option key={opt} value={opt}>{opt || `Select ${field.label}`}</option>
            ))}
          </select>
        );

      case 'dynamic_enum':
        const options = dynamicOptions[field.name] || [];
        const isObjectOptions = options.length > 0 && typeof options[0] === 'object';
        return (
          <div className="dynamic-enum-container">
            <div className="dynamic-enum-row">
              <select
                className="form-select dynamic-enum-select"
                value={value}
                onChange={(e) => handleInputChange(field.name, e.target.value)}
              >
                <option value="">Select {field.label}</option>
                {isObjectOptions
                  ? options.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.name}</option>
                    ))
                  : options.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))
                }
              </select>
              {!isObjectOptions && (
                <button
                  type="button"
                  className="add-option-btn"
                  onClick={() => setShowAddOption(prev => ({ ...prev, [field.name]: !prev[field.name] }))}
                  title="Add new option"
                >
                  +
                </button>
              )}
            </div>
            {showAddOption[field.name] && !isObjectOptions && (
              <div className="add-option-form">
                <input
                  type="text"
                  className="form-input add-option-input"
                  placeholder={`New ${field.label}`}
                  value={newOptionValue[field.name] || ''}
                  onChange={(e) => setNewOptionValue(prev => ({ ...prev, [field.name]: e.target.value }))}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addNewDynamicOption(field.name, field.options_source);
                    }
                  }}
                />
                <button
                  type="button"
                  className="save-option-btn"
                  onClick={() => addNewDynamicOption(field.name, field.options_source)}
                >
                  Add
                </button>
              </div>
            )}
          </div>
        );

      case 'project_relate':
        const projectOptions = dynamicOptions.projects || [];
        return (
          <select
            className="form-select"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
          >
            <option value="">Select {field.label}</option>
            {projectOptions.map(proj => (
              <option key={proj.id} value={proj.id}>{proj.name}</option>
            ))}
          </select>
        );

      case 'bool':
        return (
          <label className="form-checkbox-label">
            <input
              type="checkbox"
              checked={value === '1' || value === true || value === 'true'}
              onChange={(e) => handleInputChange(field.name, e.target.checked ? '1' : '0')}
            />
            <span>{field.label}</span>
          </label>
        );

      case 'date':
        return (
          <input
            type="date"
            className="form-input"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
          />
        );

      case 'datetime':
        return (
          <input
            type="datetime-local"
            className="form-input"
            value={value ? value.replace(' ', 'T').slice(0, 16) : ''}
            onChange={(e) => handleInputChange(field.name, e.target.value.replace('T', ' '))}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            className="form-input"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.label}
          />
        );

      case 'currency':
        return (
          <input
            type="number"
            className="form-input"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.label}
            step="0.01"
            min="0"
          />
        );

      case 'email':
        return (
          <input
            type="email"
            className="form-input"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.label}
          />
        );

      case 'url':
        return (
          <input
            type="url"
            className="form-input"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.label}
          />
        );

      case 'phone':
        return (
          <input
            type="tel"
            className="form-input"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.label}
          />
        );

      default: // text
        return (
          <input
            type="text"
            className="form-input"
            value={value}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.label}
          />
        );
    }
  };

  return (
    <div className="module-page">
      <div className="module-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Dashboard
        </button>
        <div className="module-title-section">
          <HomeButton onClick={onBack} />
          <span className="module-icon-large">{getModuleIcon(moduleName)}</span>
          <h1 className="module-title">{getModuleDisplayName(moduleName)}</h1>
        </div>
        <div className="header-controls">
          {moduleName === 'AOS_Products' && categories.length > 0 && (
            <select
              className="category-filter"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          )}
          <button className="refresh-button" onClick={fetchRecords} disabled={loading}>
            {loading ? '⟳' : '↻'} Refresh
          </button>
        </div>
      </div>

      <div className="module-content">
        <div className="vertical-tabs">
          <button className="vertical-tab new-record-tab" onClick={handleNewRecord}>
            <span className="tab-icon">➕</span>
            <span className="tab-label">New {getModuleSingularName(moduleName)}</span>
          </button>
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`vertical-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
          <div className="tab-spacer" />
        </div>

        <div className="tab-content">
          {activeTab === 'list' && (
            <>
              {/* Search Bar */}
              {!loading && !error && (allRecords.length > 0 || records.length > 0) && (
                <div className="search-bar-container">
                  <div className="search-input-wrapper">
                    <span className="search-icon">🔍</span>
                    <input
                      type="text"
                      className="search-input"
                      placeholder={`Search ${getModuleDisplayName(moduleName)}...`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                      <button
                        className="search-clear-btn"
                        onClick={() => setSearchQuery('')}
                        title="Clear search"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {moduleName === 'Contacts' && contactRoles.length > 0 && (
                    <select
                      className="category-filter"
                      value={selectedContactRole}
                      onChange={(e) => setSelectedContactRole(e.target.value)}
                    >
                      <option value="">All Roles</option>
                      {contactRoles.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  )}
                  {searchQuery && (
                    <span className="search-results-count">
                      {filteredRecords.length} of {records.length} records
                    </span>
                  )}
                </div>
              )}

              {loading && (
                <div className="module-loading">
                  <div className="spinner"></div>
                  <p>Loading {getModuleDisplayName(moduleName)}...</p>
                </div>
              )}

              {error && (
                <div className="module-error">
                  <p>{error}</p>
                  <button onClick={fetchRecords}>Try Again</button>
                </div>
              )}

              {!loading && !error && records.length === 0 && (
                <div className="module-empty">
                  <span className="empty-icon">{getModuleIcon(moduleName)}</span>
                  <h3>No {getModuleDisplayName(moduleName)} Found</h3>
                  <p>There are no records in this module yet.</p>
                  <button className="create-first-btn" onClick={handleNewRecord}>
                    Create First Record
                  </button>
                </div>
              )}

              {!loading && !error && records.length > 0 && filteredRecords.length === 0 && searchQuery && (
                <div className="module-empty">
                  <span className="empty-icon">🔍</span>
                  <h3>No Results Found</h3>
                  <p>No records match "{searchQuery}"</p>
                  <button className="create-first-btn" onClick={() => setSearchQuery('')}>
                    Clear Search
                  </button>
                </div>
              )}

              {!loading && !error && filteredRecords.length > 0 && (
                <>
                  {moduleName === 'Leads' ? (() => {
                    const statusOrder = ['Form Submission', 'Discovery Conducted', 'Consultation Pending', 'LOA Sent', 'Converted', 'Declined'];
                    const grouped = {};
                    statusOrder.forEach(s => { grouped[s] = []; });
                    grouped['No Status'] = [];
                    filteredRecords.forEach(record => {
                      const status = record.attributes?.status || 'No Status';
                      if (!grouped[status]) grouped[status] = [];
                      grouped[status].push(record);
                    });
                    const collapsible = ['Converted', 'Declined'];

                    return Object.entries(grouped)
                      .filter(([, records]) => records.length > 0)
                      .map(([status]) => {
                        const isCollapsible = collapsible.includes(status);
                        const isHidden = isCollapsible && hiddenStatusGroups[status];
                        return (
                          <div key={status} className={`leads-status-group ${isHidden ? 'collapsed' : ''}`}>
                            <div className="leads-status-group-header">
                              <span className={`record-status-badge status-${status.toLowerCase().replace(/\s+/g, '-')}`}>
                                {status}
                              </span>
                              <span className="leads-status-count">{grouped[status].length}</span>
                              {isCollapsible && (
                                <button
                                  className="leads-group-toggle"
                                  onClick={() => setHiddenStatusGroups(prev => ({ ...prev, [status]: !prev[status] }))}
                                >
                                  {isHidden ? 'Show' : 'Hide'}
                                </button>
                              )}
                            </div>
                            {!isHidden && (
                              <div className="records-grid">
                                {grouped[status].map((record) => {
                                  const displayFields = getDisplayFields(record);
                                  return (
                                    <div
                                      key={record.id}
                                      className="record-card"
                                      onClick={() => handleEditRecord(record)}
                                    >
                                      <div className="record-header">
                                        <span className="record-icon">{getModuleIcon(moduleName)}</span>
                                        <select
                                          className={`lead-status-select status-${(record.attributes?.status || 'no-status').toLowerCase().replace(/\s+/g, '-')}`}
                                          value={record.attributes?.status || ''}
                                          disabled={savingStatusId === record.id}
                                          title="Change status"
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) => { e.stopPropagation(); updateLeadStatus(record, e.target.value); }}
                                        >
                                          {!record.attributes?.status && <option value="">No Status</option>}
                                          {record.attributes?.status && !LEAD_STATUSES.includes(record.attributes.status) && (
                                            <option value={record.attributes.status}>{record.attributes.status}</option>
                                          )}
                                          {LEAD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                        {savingStatusId === record.id && <span className="lead-status-saving">saving…</span>}
                                        <span className="record-id">#{record.id.slice(0, 8)}</span>
                                      </div>
                                      <div className="record-body">
                                        {Object.entries(displayFields).map(([key, value]) => (
                                          <div key={key} className="record-field">
                                            <span className="field-label">{key}:</span>
                                            <span className="field-value">{formatFieldValue(value)}</span>
                                          </div>
                                        ))}
                                      </div>
                                      <div className="record-card-footer">
                                        <span className="edit-hint">Click to edit</span>
                                        <button
                                          className="delete-btn-small"
                                          onClick={(e) => handleDeleteClick(e, record)}
                                          disabled={deleting === record.id}
                                          title="Delete record"
                                        >
                                          {deleting === record.id ? '...' : 'Delete'}
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      });
                  })() : moduleName === 'Tasks' ? (() => {
                    const priorityOrder = ['High', 'Medium', 'Low'];
                    const statusOrder = ['Not Started', 'In Progress', 'Completed', 'Pending Input', 'Deferred'];
                    // Group by priority, then by status within each priority
                    const grouped = {};
                    priorityOrder.forEach(p => {
                      grouped[p] = {};
                      statusOrder.forEach(s => { grouped[p][s] = []; });
                      grouped[p]['No Status'] = [];
                    });
                    grouped['No Priority'] = {};
                    statusOrder.forEach(s => { grouped['No Priority'][s] = []; });
                    grouped['No Priority']['No Status'] = [];

                    filteredRecords.forEach(record => {
                      const priority = record.attributes?.priority || 'No Priority';
                      const status = record.attributes?.status || 'No Status';
                      if (!grouped[priority]) {
                        grouped[priority] = {};
                        statusOrder.forEach(s => { grouped[priority][s] = []; });
                        grouped[priority]['No Status'] = [];
                      }
                      if (!grouped[priority][status]) grouped[priority][status] = [];
                      grouped[priority][status].push(record);
                    });

                    return Object.entries(grouped)
                      .filter(([, statuses]) => Object.values(statuses).some(arr => arr.length > 0))
                      .map(([priority, statuses]) => {
                        const priorityCount = Object.values(statuses).reduce((sum, arr) => sum + arr.length, 0);
                        return (
                          <div key={priority} className="task-priority-group">
                            <div className="task-priority-header">
                              <span className={`task-priority-badge priority-${priority.toLowerCase().replace(/\s+/g, '-')}`}>
                                {priority}
                              </span>
                              <span className="leads-status-count">{priorityCount}</span>
                            </div>
                            {Object.entries(statuses)
                              .filter(([, recs]) => recs.length > 0)
                              .map(([status, recs]) => (
                                <div key={status} className="task-status-subgroup">
                                  <div className="task-status-subheader">
                                    <span className={`task-status-badge status-${status.toLowerCase().replace(/\s+/g, '-')}`}>
                                      {status}
                                    </span>
                                    <span className="leads-status-count">{recs.length}</span>
                                  </div>
                                  <div className="records-grid">
                                    {recs.map((record) => {
                                      const displayFields = getDisplayFields(record);
                                      const proj = taskProjects[record.id];
                                      const createdById = record.attributes?.created_by;
                                      const createdByUser = createdById ? (dynamicOptions['created_by'] || dynamicOptions['assigned_user_id'] || []).find(u => u.id === createdById) : null;
                                      return (
                                        <div
                                          key={record.id}
                                          className="record-card"
                                          onClick={() => handleEditRecord(record)}
                                        >
                                          <div className="record-header">
                                            <span className="record-icon">{getModuleIcon(moduleName)}</span>
                                            {proj?.project_name && (
                                              <span className="task-project-badge">{proj.project_name}</span>
                                            )}
                                            <span className={`task-priority-badge priority-${(record.attributes?.priority || 'none').toLowerCase().replace(/\s+/g, '-')}`}>
                                              {record.attributes?.priority || 'No Priority'}
                                            </span>
                                            <span className={`task-status-badge status-${(record.attributes?.status || 'no-status').toLowerCase().replace(/\s+/g, '-')}`}>
                                              {record.attributes?.status || 'No Status'}
                                            </span>
                                          </div>
                                          <div className="record-body">
                                            {Object.entries(displayFields).map(([key, value]) => (
                                              <div key={key} className="record-field">
                                                <span className="field-label">{key}:</span>
                                                <span className="field-value">{formatFieldValue(value)}</span>
                                              </div>
                                            ))}
                                            {record.attributes?.room_name && (
                                              <div className="record-field">
                                                <span className="field-label">Room:</span>
                                                <span className="field-value">{record.attributes.room_name}</span>
                                              </div>
                                            )}
                                            {createdByUser && (
                                              <div className="record-field">
                                                <span className="field-label">Created By:</span>
                                                <span className="field-value">{createdByUser.name}</span>
                                              </div>
                                            )}
                                          </div>
                                          <div className="record-card-footer">
                                            <span className="edit-hint">Click to edit</span>
                                            <button
                                              className="delete-btn-small"
                                              onClick={(e) => handleDeleteClick(e, record)}
                                              disabled={deleting === record.id}
                                              title="Delete record"
                                            >
                                              {deleting === record.id ? '...' : 'Delete'}
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                          </div>
                        );
                      });
                  })() : moduleName === 'Contacts' ? (() => {
                    // Group by Contact Role (contact_role_c). Empty role -> "Unassigned".
                    const grouped = {};
                    filteredRecords.forEach(record => {
                      const role = (record.attributes?.contact_role_c || '').trim() || 'Unassigned';
                      if (!grouped[role]) grouped[role] = [];
                      grouped[role].push(record);
                    });
                    const order = Object.keys(grouped).sort((a, b) => {
                      if (a === 'Unassigned') return 1;
                      if (b === 'Unassigned') return -1;
                      return a.localeCompare(b);
                    });
                    return order.map(role => (
                      <div key={role} className="contacts-role-group">
                        <div className="contacts-role-group-header">
                          <span className="contacts-role-name">{role}</span>
                          <span className="contacts-role-count">{grouped[role].length}</span>
                        </div>
                        <div className="records-grid">
                          {grouped[role].map((record) => {
                            const displayFields = getDisplayFields(record);
                            return (
                              <div
                                key={record.id}
                                className="record-card"
                                onClick={() => handleEditRecord(record)}
                              >
                                <div className="record-header">
                                  <span className="record-icon">{getModuleIcon(moduleName)}</span>
                                  <span className="record-id">#{record.id.slice(0, 8)}</span>
                                </div>
                                <div className="record-body">
                                  {Object.entries(displayFields).map(([key, value]) => (
                                    <div key={key} className="record-field">
                                      <span className="field-label">{key}:</span>
                                      <span className="field-value">{formatFieldValue(value)}</span>
                                    </div>
                                  ))}
                                </div>
                                <div className="record-card-footer">
                                  <span className="edit-hint">Click to edit</span>
                                  <button
                                    className="delete-btn-small"
                                    onClick={(e) => handleDeleteClick(e, record)}
                                    disabled={deleting === record.id}
                                    title="Delete record"
                                  >
                                    {deleting === record.id ? '...' : 'Delete'}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })() : (
                  <div className="records-grid">
                    {filteredRecords.map((record) => {
                      const displayFields = getDisplayFields(record);

                      const meetingClass = moduleName === 'Meetings' ? meetingTileClass(record) : '';
                      return (
                        <div
                          key={record.id}
                          className={`record-card${meetingClass ? ' ' + meetingClass : ''}`}
                          onClick={() => handleEditRecord(record)}
                        >
                          <div className="record-header">
                            <span className="record-icon">{getModuleIcon(moduleName)}</span>
                            <span className="record-id">#{record.id.slice(0, 8)}</span>
                          </div>
                          <div className="record-body">
                            {Object.entries(displayFields).map(([key, value]) => (
                              <div key={key} className="record-field">
                                <span className="field-label">{key}:</span>
                                <span className="field-value">{formatFieldValue(value)}</span>
                              </div>
                            ))}
                          </div>
                          <div className="record-card-footer">
                            <span className="edit-hint">Click to edit</span>
                            <button
                              className="delete-btn-small"
                              onClick={(e) => handleDeleteClick(e, record)}
                              disabled={deleting === record.id}
                              title="Delete record"
                            >
                              {deleting === record.id ? '...' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  )}

                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <span className="page-info">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === 'edit' && (moduleName === 'AOS_Invoices' || moduleName === 'AOS_Quotes' || moduleName === 'jcrm_PurchaseOrders') && editingRecord ? (
            <InvoiceLedgerEdit
              recordId={editingRecord.id}
              mode={
                moduleName === 'AOS_Quotes' ? 'quote'
                : moduleName === 'jcrm_PurchaseOrders' ? 'purchase_order'
                : 'invoice'
              }
              onCancel={handleCancelEdit}
              onSaved={() => { handleCancelEdit(); fetchRecords(); }}
            />
          ) : activeTab === 'edit' && moduleName === 'Tasks' && editingRecord ? (
            <div className="edit-form-container task-detail-container">
              <div className="edit-form-header">
                {taskStack.length > 0 && (
                  <button className="cancel-btn" style={{ marginRight: '0.75rem' }} onClick={backToParentTask}>
                    ← {taskStack[taskStack.length - 1].attributes?.name || 'parent task'}
                  </button>
                )}
                <h2>Edit {taskStack.length > 0 ? 'Subtask' : getModuleSingularName(moduleName)}</h2>
                <span className="editing-id">ID: {editingRecord.id.slice(0, 8)}...</span>
              </div>

              {saveMessage && (
                <div className={`save-message ${saveMessage.type}`}>{saveMessage.text}</div>
              )}

              <div className="task-detail-two-pane">
                {/* Left: task details in a table */}
                <div className="task-detail-left">
                  <table className="task-detail-table">
                    <tbody>
                      {moduleFields
                        .filter(field => field.name !== 'description'
                          && (!field.show_when || Object.entries(field.show_when).every(([k, v]) => formData[k] === v)))
                        .map(field => (
                          <tr key={field.name}>
                            <td className="lbl">
                              {field.label}
                              {field.required && <span className="required">*</span>}
                            </td>
                            <td>{renderFormField(field)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                  <div className="edit-form-actions">
                    <span className={`form-autosave form-autosave-${autoStatus}`} style={{ marginRight: 'auto' }}>{autoSaveLabel(autoStatus)}</span>
                    <button className="cancel-btn" onClick={handleCancelEdit} disabled={saving}>Close</button>
                    <button className="save-btn" onClick={handleSaveRecord} disabled={saving}>{saving ? 'Saving...' : 'Done'}</button>
                  </div>
                </div>

                {/* Right: multi-tab Notes + the task conversation (same as the project Tasks view) */}
                <div className="task-detail-right">
                  <h3 className="task-detail-notes-title">Notes</h3>
                  <PersistentTabNotes
                    parentType="task"
                    parentId={editingRecord.id}
                    defaultTitle="Notes"
                    mentionContext={{ projectId: formData.project_id || null, projectName: '' }}
                  />

                  <h3 className="task-detail-notes-title" style={{ marginTop: '1.75rem' }}>Conversation</h3>
                  <div className="task-conversations">
                    <div className="task-conv-group">
                      <div className="task-conv-group-header">
                        <span className="task-conv-recipient">@{(editingRecord.attributes?.assigned_user_name || '').trim() || 'Unassigned'}</span>
                        <span className="task-conv-count">1</span>
                      </div>
                      <div className="task-conv-thread">
                        <TaskConversationBubble
                          task={{ id: editingRecord.id, attributes: editingRecord.attributes || {}, responses: taskResponses }}
                          currentUser={currentUser}
                          onEdit={() => {}}
                          onDelete={deleteThisTask}
                          onResponsesChanged={loadTaskResponses}
                          fmtDate={(d) => d ? new Date(d).toLocaleDateString() : ''}
                          fmtDateTime={(d) => d ? new Date(d).toLocaleString() : ''}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <TaskSubtasks
                parentTask={{ id: editingRecord.id, attributes: editingRecord.attributes || {} }}
                currentUser={currentUser}
                taskUsers={dynamicOptions['assigned_user_id'] || dynamicOptions['created_by'] || []}
                onOpenSubtask={openStandaloneSubtask}
                onChanged={fetchRecords}
              />
            </div>
          ) : activeTab === 'edit' && (
            <div className="edit-form-container">
              <div className="edit-form-header">
                <h2>{editingRecord ? `Edit ${getModuleSingularName(moduleName)}` : `Create New ${getModuleSingularName(moduleName)}`}</h2>
                {editingRecord && (
                  <span className="editing-id">ID: {editingRecord.id.slice(0, 8)}...</span>
                )}
              </div>

              {saveMessage && (
                <div className={`save-message ${saveMessage.type}`}>
                  {saveMessage.text}
                </div>
              )}

              <div className="edit-form">
                {(() => {
                  const visibleFields = moduleFields.filter(field => {
                    if (!field.show_when) return true;
                    return Object.entries(field.show_when).every(
                      ([k, v]) => formData[k] === v
                    );
                  });
                  return visibleFields.map((field, idx) => {
                    // Group half_width fields side by side
                    const nextField = visibleFields[idx + 1];
                    if (field.half_width && nextField?.half_width) {
                      return (
                        <div key={field.name} className="form-group-row">
                          <div className={`form-group form-group-half ${field.type === 'bool' ? 'form-group-checkbox' : ''}`}>
                            {field.type !== 'bool' && (
                              <label className="form-label">{field.label}</label>
                            )}
                            {renderFormField(field)}
                          </div>
                          <div className={`form-group form-group-half ${nextField.type === 'bool' ? 'form-group-checkbox' : ''}`}>
                            {nextField.type !== 'bool' && (
                              <label className="form-label">{nextField.label}</label>
                            )}
                            {renderFormField(nextField)}
                          </div>
                        </div>
                      );
                    }
                    // Skip the second half_width field (already rendered above)
                    if (field.half_width && idx > 0 && visibleFields[idx - 1]?.half_width) {
                      return null;
                    }
                    return (
                      <div
                        key={field.name}
                        className={`form-group ${field.type === 'bool' ? 'form-group-checkbox' : ''}`}
                      >
                        {field.type !== 'bool' && (
                          <label className="form-label">
                            {field.label}
                            {field.required && <span className="required">*</span>}
                          </label>
                        )}
                        {renderFormField(field)}
                      </div>
                    );
                  });
                })()}
              </div>

              {moduleName === 'Leads' && editingRecord?.id && (
                <div className="lead-scheduler">
                  <div className="lead-scheduler-head">
                    <h4>Schedule a Meeting</h4>
                  </div>
                  <p className="lead-scheduler-hint">
                    Creates a meeting from this lead — name, phone, address and notes are filled in automatically.
                  </p>
                  <div className="lead-scheduler-row">
                    <label className="lead-scheduler-field lead-scheduler-field-grow">
                      <span>Meeting title prefix</span>
                      <input
                        type="text"
                        value={mtgPrefix}
                        onChange={(e) => setMtgPrefix(e.target.value)}
                        placeholder="e.g. Consultation"
                      />
                    </label>
                  </div>
                  {(() => {
                    const pfx = (mtgPrefix || '').trim();
                    const nm = `${formData?.first_name || ''} ${formData?.last_name || ''}`.trim() || 'Lead';
                    return (
                      <p className="lead-scheduler-hint">
                        Meeting title: <strong>{pfx ? `${pfx} - ${nm}` : `Meeting with ${nm}`}</strong>
                      </p>
                    );
                  })()}
                  <div className="lead-scheduler-row">
                    <label className="lead-scheduler-field">
                      <span>Date &amp; time</span>
                      <input
                        type="datetime-local"
                        value={mtgWhen}
                        onChange={(e) => setMtgWhen(e.target.value)}
                      />
                    </label>
                    <label className="lead-scheduler-field">
                      <span>Duration</span>
                      <div className="lead-scheduler-duration">
                        <select value={mtgDurH} onChange={(e) => setMtgDurH(e.target.value)} aria-label="Hours">
                          {[0, 1, 2, 3, 4, 5, 6, 7, 8].map(h => <option key={h} value={h}>{h} hr</option>)}
                        </select>
                        <select value={mtgDurM} onChange={(e) => setMtgDurM(e.target.value)} aria-label="Minutes">
                          {[0, 15, 30, 45].map(m => <option key={m} value={m}>{m} min</option>)}
                        </select>
                      </div>
                    </label>
                    <button
                      type="button"
                      className="lead-scheduler-btn"
                      onClick={scheduleLeadMeeting}
                      disabled={mtgSaving || !mtgWhen}
                    >
                      {mtgSaving ? 'Scheduling…' : '📅 Schedule Meeting'}
                    </button>
                  </div>
                  {mtgMsg && <div className={`lead-scheduler-msg ${mtgMsg.type}`}>{mtgMsg.text}</div>}
                </div>
              )}

              {moduleName === 'Leads' && editingRecord?.id && (
                <div className="lead-attachments">
                  <div className="lead-attachments-head">
                    <h4>Attachments</h4>
                    <input
                      ref={attInputRef}
                      type="file"
                      accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                      style={{ display: 'none' }}
                      onChange={handleAttachChosen}
                    />
                    <button
                      type="button"
                      className="lead-attach-btn"
                      disabled={uploadingAtt}
                      onClick={() => attInputRef.current && attInputRef.current.click()}
                    >
                      {uploadingAtt ? 'Uploading…' : '+ Attach file'}
                    </button>
                  </div>
                  {attachments.length === 0 ? (
                    <p className="lead-attachments-empty">No files attached yet.</p>
                  ) : (
                    <ul className="lead-attachments-list">
                      {attachments.map(att => (
                        <li key={att.id} className="lead-attachment">
                          <a href={att.file_url} target="_blank" rel="noopener noreferrer" title={att.name}>
                            <span className="att-icon">{attIcon(att.mime)}</span>
                            <span className="att-name">{att.name}</span>
                          </a>
                          <button type="button" className="att-del" onClick={() => deleteAttachment(att.id)} title="Remove attachment">×</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="edit-form-actions">
                {editingRecord && (
                  <span className={`form-autosave form-autosave-${autoStatus}`} style={{ marginRight: 'auto' }}>{autoSaveLabel(autoStatus)}</span>
                )}
                <button
                  className="cancel-btn"
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  {editingRecord ? 'Close' : 'Cancel'}
                </button>
                <button
                  className="save-btn"
                  onClick={handleSaveRecord}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : (editingRecord ? 'Done' : 'Create Record')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete-modal-overlay" onClick={handleDeleteCancel}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <span className="delete-modal-icon">Warning</span>
              <h3>Delete Record?</h3>
            </div>
            <div className="delete-modal-body">
              <p>Are you sure you want to delete this record?</p>
              <p className="delete-record-name">
                {showDeleteConfirm.attributes?.name ||
                 `${showDeleteConfirm.attributes?.first_name || ''} ${showDeleteConfirm.attributes?.last_name || ''}`.trim() ||
                 `Record #${showDeleteConfirm.id.slice(0, 8)}`}
              </p>
              <p className="delete-warning">This action cannot be undone.</p>
            </div>
            <div className="delete-modal-actions">
              <button
                className="delete-modal-cancel"
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="delete-modal-confirm"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showConvertDialog && (
        <div className="delete-modal-overlay" onClick={() => { setShowConvertDialog(false); setConvertPending(null); }}>
          <div className="delete-modal convert-project-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header convert-header">
              <span className="convert-modal-icon">🏗️</span>
              <h3>Convert Lead?</h3>
            </div>
            <div className="delete-modal-body">
              <p>
                Yes will create an <strong>Account</strong>, <strong>Contact</strong>,
                <strong> Opportunity</strong> and <strong>Project</strong> from this lead
                (billing address copied to the Account &amp; Contact, notes copied to the Project).
              </p>
              <label className="convert-name-label">
                Name
                <input
                  type="text"
                  className="convert-name-input"
                  value={convertName}
                  onChange={(e) => setConvertName(e.target.value)}
                  placeholder="This Lead"
                />
              </label>
            </div>
            <div className="delete-modal-actions">
              <button
                className="delete-modal-cancel"
                onClick={handleConvertNo}
              >
                No
              </button>
              <button
                className="convert-modal-confirm"
                onClick={handleConvertYes}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModulePage;
